import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAppConfig } from "@config/appConfig";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import LocationIcon from "@components/Icons/LocationIcon";
import SearchSelect from "@components/SearchSelect";
import Button from "@components/Ui/Button";
import LazyImage from "@components/Ui/LazyImage";
import Loader from "@components/Ui/Loader";
import { getDistance, requestUserLocation } from "@utils/locationUtils";
import "./style.scss";

const V3ParticipatingCinemas = () => {
    const { userCoords, setUserCoords } = useContext(AuthContext);
    const { cinemas, cinemasLoading } = useContext(ApplicationContext);
    const config = getAppConfig();

    const [selectedVenue, setSelectedVenue] = useState(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const fallbackImage = `${config?.cdnUrl}asset/img/default-movie-poster-small.webp`;



    const searchOptions = useMemo(
        () =>
            cinemas.map((item) => ({
                ...item,
                label: item.name,
                value: item.name,
            })),
        [cinemas]
    );

    const filterOptions = (candidate, input) => {
        if (!candidate.value || typeof candidate.value !== "string") return false;
        const candidateStr = candidate.value.toLowerCase();
        const candidateTerms = candidateStr.split(" ");
        const searchStr = input.toLowerCase();
        return searchStr && searchStr.includes(" ")
            ? candidateStr.includes(searchStr)
            : searchStr
                ? candidateTerms.some((str) => str.startsWith(searchStr))
                : false;
    };

    const handleOnChangeVenue = (option, { action }) => {
        if (action === "clear") {
            setSelectedVenue(null);
        } else if (action === "select-option") {
            setSelectedVenue(option);
        }
    };

    const customNoOptionsMessage = () => (
        <span className="venue-finder__no-results">We did not find any venues</span>
    );

    const handleUseCurrentLocation = () => {
        setIsGettingLocation(true);
        requestUserLocation((coords) => {
            setIsGettingLocation(false);
            if (coords) setUserCoords(coords);
        });
    };

    const sortedCinemas = useMemo(() => {
        if (!userCoords || !cinemas) return cinemas;
        return [...cinemas]
            .map((cinema) => {
                const distance = getDistance(
                    userCoords.latitude,
                    userCoords.longitude,
                    cinema.latitude,
                    cinema.longitude
                );
                return { ...cinema, distance };
            })
            .sort((a, b) => a.distance - b.distance);
    }, [userCoords, cinemas]);

    const filteredCinemas = selectedVenue
        ? sortedCinemas.filter((cinema) => cinema.name === selectedVenue.label)
        : sortedCinemas;

    if (cinemasLoading || isGettingLocation) {
        return <Loader isLoading />;
    }

    if (!cinemas || cinemas.length === 0) {
        return null;
    }

    return (
        <div className="participating-cinemas__container">
            <h1 className="main-heading">Cinemas</h1>
            <div className="participating-cinemas__filters">
                <div className="participating-cinemas__input">
                    <SearchSelect
                        placeholder="Enter your cinema"
                        aria-label="Enter your cinema"
                        value={selectedVenue}
                        options={searchOptions}
                        filterOption={filterOptions}
                        onChange={handleOnChangeVenue}
                        isClearable={true}
                        components={{
                            DropdownIndicator: () => null,
                            IndicatorSeparator: () => null,
                        }}
                        openMenuOnClick={false}
                        noOptionsMessage={customNoOptionsMessage}
                    />
                </div>
                <div className="participating-cinemas__location-wrapper">
                    <LocationIcon className="participating-cinemas__icon" />
                    <Button
                        additionalClasses="link participating-cinemas__location-button"
                        onClick={handleUseCurrentLocation}
                    >
                        Use current location
                    </Button>
                </div>
            </div>

            <div className="participating-cinemas__list">
                {filteredCinemas.map((cinema) => (
                    <Link
                        key={cinema.id}
                        className="participating-cinemas__card"
                        to={{ pathname: cinema.link_url }}
                    >
                        <div className="participating-cinemas__image-wrapper">
                            <LazyImage
                                className="participating-cinemas__image"
                                src={cinema.thumbnail_url ?? cinema.large_image_url}
                                alt={cinema.name}
                                fallbackSrc={fallbackImage}
                                fetchpriority="low"
                            />
                        </div>
                        <div className="participating-cinemas__info">
                            <h2 className="participating-cinemas__title">
                                {cinema.name}
                                {Number.isFinite(cinema.distance) && (
                                    <span className="participating-cinemas__distance">
                                        {" "}({cinema.distance.toFixed(1)} km)
                                    </span>
                                )}
                            </h2>
                            <address className="participating-cinemas__address">
                                {[cinema.info_address, cinema.info_state, cinema.info_postcode]
                                    .filter(Boolean)
                                    .join(", ")}
                            </address>
                        </div>
                        <span className="participating-cinemas__background"></span>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default V3ParticipatingCinemas;
